import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { EmailInvoice } from '../email/EmailInvoice';
import { Invoice } from '../../types/invoice';
import { User } from '../../types';
import { supabase } from '../../lib/supabase';
import { invoiceTranslations, getPaymentMethodTranslation, formatCurrency, formatDate } from '../../utils/invoiceTranslations';
import { X, Download, Printer, Mail } from 'lucide-react';

interface InvoiceGeneratorProps {
  invoice: Invoice;
  user: User;
  onClose: () => void;
  onEmailSent?: () => void;
}

export const InvoiceGenerator: React.FC<InvoiceGeneratorProps> = ({
  invoice,
  user,
  onClose,
  onEmailSent
}) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [userIcon, setUserIcon] = useState<string | null>(null);
  
  const t = invoiceTranslations[invoice.language as 'en' | 'nl'];

  useEffect(() => {
    const loadIcon = async () => {
      if (user?.id) {
        try {
          const { data: settings } = await supabase
            .from('user_settings')
            .select('avatar_data')
            .eq('user_id', user.id)
            .maybeSingle();

          if (settings?.avatar_data) {
            setUserIcon(settings.avatar_data);
          }
        } catch (error) {
          console.error('Failed to load avatar:', error);
        }
      }
    };
    loadIcon();
  }, [user?.id]);

  const handlePrint = () => {
    window.print();
  };

  const downloadAsPDF = () => {
    // Create a clean version for PDF
    const printContent = document.getElementById('invoice-content')?.innerHTML;
    if (!printContent) return;

    const pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      pdfWindow.document.write(`
        <html>
          <head>
            <title>Invoice ${invoice.invoice_number}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
              .invoice-container { max-width: 800px; margin: 0 auto; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
              th { background-color: #f5f5f5; font-weight: bold; }
              .text-right { text-align: right; }
              .font-bold { font-weight: bold; }
              .text-lg { font-size: 1.125rem; }
              .text-2xl { font-size: 1.5rem; }
              .mb-4 { margin-bottom: 1rem; }
              .mb-6 { margin-bottom: 1.5rem; }
              .mb-8 { margin-bottom: 2rem; }
              .grid { display: grid; }
              .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
              .gap-8 { gap: 2rem; }
              .border-t { border-top: 2px solid #000; }
              .pt-4 { padding-top: 1rem; }
              @media print {
                body { margin: 0; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="invoice-container">
              ${printContent}
            </div>
          </body>
        </html>
      `);
      pdfWindow.document.close();
      
      // Trigger download
      setTimeout(() => {
        pdfWindow.print();
      }, 500);
    }
  };

  const handleEmailSent = async (email: string, subject: string, message: string) => {
    // Simulate email sending
    console.log('Sending invoice email:', { email, subject, message });
    
    // In real implementation, would call email service
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (onEmailSent) {
      onEmailSent();
    }
    
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto z-50">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl">
          {/* Header Controls */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 no-print">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t.invoice} {invoice.invoice_number}
            </h2>
            
            <div className="flex space-x-3">
              {invoice.customer_email && (
                <Button
                  onClick={() => setShowEmailModal(true)}
                  variant="secondary"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
              )}
              
              <Button onClick={downloadAsPDF} variant="secondary">
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              
              <Button onClick={handlePrint} variant="secondary">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Invoice Content */}
          <div id="invoice-content" className="p-8">
            {/* Header with Logo and Company Info */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              {/* Company Logo and Info */}
              <div>
                {/* Company Logo */}
                <div className="mb-6">
                  {userIcon ? (
                    <img
                      src={userIcon}
                      alt="Company Logo"
                      className="h-20 w-auto object-contain"
                    />
                  ) : (
                    <div className="h-20 w-32 bg-transparent"></div>
                  )}
                </div>
                
                {/* Company Details */}
                <div className="space-y-1">
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {user.company_name || user.username}
                  </div>
                  {user.address && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {user.address}
                    </div>
                  )}
                  {user.kvk_number && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t.kvkNumber}: {user.kvk_number}
                    </div>
                  )}
                  {user.vat_number && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t.vatNumber}: {user.vat_number}
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Info */}
              <div className="text-right">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                  {t.invoice}
                </h1>
                
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t.invoiceNumber}: </span>
                    <span className="font-medium">{invoice.invoice_number}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t.invoiceDate}: </span>
                    <span className="font-medium">{formatDate(invoice.invoice_date, invoice.language as 'en' | 'nl')}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t.dueDate}: </span>
                    <span className="font-medium">{formatDate(invoice.due_date, invoice.language as 'en' | 'nl')}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t.paymentTerms}: </span>
                    <span className="font-medium">{user.payment_terms_days || 14} {invoice.language === 'nl' ? 'dagen' : 'days'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                {t.billTo}
              </h3>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {invoice.customer_name}
                </div>
                {invoice.customer_address && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line mt-1">
                    {invoice.customer_address}
                  </div>
                )}
                {invoice.customer_vat_number && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t.vatNumber}: {invoice.customer_vat_number}
                  </div>
                )}
                {invoice.customer_email && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Email: {invoice.customer_email}
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Items Table */}
            <div className="mb-8">
              <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-700">
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold">
                      {t.description}
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold">
                      {t.color}
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold">
                      {t.sku}
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center font-semibold">
                      {t.quantity}
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-semibold">
                      {t.unitPrice}
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-semibold">
                      {t.totalPrice}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.invoice_items?.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                        {item.description}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                        {item.color_name || '—'}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                        {item.sku || '—'}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center">
                        {item.quantity}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right">
                        {formatCurrency(item.unit_price, invoice.currency)}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right">
                        {formatCurrency(item.total_price, invoice.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-80">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t.subtotal}:</span>
                    <span className="font-medium">{formatCurrency(invoice.subtotal_amount, invoice.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t.vat} ({user.tax_percentage || 21}%):</span>
                    <span className="font-medium">{formatCurrency(invoice.tax_amount, invoice.currency)}</span>
                  </div>
                  <div className="border-t border-gray-300 dark:border-gray-600 pt-2">
                    <div className="flex justify-between text-lg font-bold">
                      <span>{t.total}:</span>
                      <span className="text-green-600 dark:text-green-400">
                        {formatCurrency(invoice.total_amount, invoice.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {t.paymentInstructions}
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">{t.paymentMethod}: </span>
                    <span className="font-medium">
                      {getPaymentMethodTranslation(invoice.payment_method, invoice.language as 'en' | 'nl')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">{t.paymentStatus}: </span>
                    <span className={`font-medium ${
                      invoice.payment_status === 'paid' ? 'text-green-600 dark:text-green-400' :
                      invoice.payment_status === 'cancelled' ? 'text-gray-600 dark:text-gray-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {invoice.payment_status === 'paid' ? t.paid :
                       invoice.payment_status === 'cancelled' ? 'Cancelled' : t.unpaid}
                    </span>
                  </div>
                  {user.bank_account && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t.bankAccount}: </span>
                      <span className="font-mono">{user.bank_account}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {t.termsAndConditions}
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {invoice.terms_and_conditions || (
                    invoice.language === 'nl' 
                      ? 'Betaling binnen 14 dagen na factuurdatum. Bij te late betaling kunnen administratiekosten in rekening worden gebracht.'
                      : 'Payment due within 14 days of invoice date. Late payment may result in additional charges.'
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
              <div>
                {invoice.language === 'nl' 
                  ? 'Bedankt voor uw vertrouwen in onze dienstverlening'
                  : 'Thank you for your business'
                }
              </div>
              <div className="mt-2">
                {invoice.language === 'nl' 
                  ? `Gegenereerd op ${formatDate(new Date(), 'nl')}`
                  : `Generated on ${formatDate(new Date(), 'en')}`
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && invoice.customer_email && (
        <EmailInvoice
          invoice={invoice}
          onClose={() => setShowEmailModal(false)}
          onSend={handleEmailSent}
        />
      )}
    </div>
  );
};